import { Dialog as DialogPrimitive } from 'radix-ui';
import { useMemo, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DialogContent } from '@/components/ui/dialog/dialog';
import { TextInput } from '@/components/ui/form/input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { DiffSection } from './diff-section';
import { type Metadata } from './inputs-db.atoms';
import { useRawInputChain } from './inputs-db.query';

interface TextViewerDialogProps {
  text: string;
  isOpen: boolean;
  onClose: () => void;
  initialSearchText?: string;
  metadata?: Metadata;
  inputId: string;
  itemType: string;
  textSize: number;
}

export const TextViewerDialog = ({
  text,
  isOpen,
  onClose,
  initialSearchText = '',
  metadata,
  inputId,
  itemType,
  textSize,
}: TextViewerDialogProps) => {
  const [searchText, setSearchText] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('content');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);

  const showMetadata = metadata && Object.keys(metadata).length > 0;
  const showDiffTab = itemType === 'input';

  const {
    data: diffData,
    isLoading: isDiffLoading,
    error: diffError,
  } = useRawInputChain(inputId, activeTab === 'diff' && isOpen && showDiffTab);

  useEffect(() => {
    if (isOpen) {
      setSearchText(initialSearchText);
      setCurrentMatchIndex(0);
      setActiveTab('content');
    }
  }, [isOpen, initialSearchText]);

  const { highlightedContent, matchCount } = useMemo(() => {
    if (!searchText.trim()) {
      return { highlightedContent: text, matchCount: 0 };
    }

    const regex = new RegExp(
      `(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi'
    );
    const matches = text.match(regex) || [];
    const count = matches.length;

    const segments = text.split(regex);
    let matchIndex = 0;
    const content = [];

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];

      if (i % 2 === 1 && regex.test(segment)) {
        const isCurrentMatch = matchIndex === currentMatchIndex;
        content.push(
          <mark
            key={`match-${i}-${matchIndex}`}
            id={isCurrentMatch ? 'current-match' : undefined}
            className={
              isCurrentMatch
                ? 'bg-blue-400 text-white'
                : 'bg-yellow-200 text-black'
            }
          >
            {segment}
          </mark>
        );
        matchIndex++;
      } else {
        content.push(segment);
      }
    }

    return { highlightedContent: content, matchCount: count };
  }, [text, searchText, currentMatchIndex]);

  useEffect(() => {
    if (matchCount > 0 && textContentRef.current) {
      const currentMatch =
        textContentRef.current.querySelector('#current-match');
      if (currentMatch) {
        currentMatch.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [currentMatchIndex, matchCount, highlightedContent]);

  const goToNextMatch = () => {
    if (matchCount > 0) {
      setCurrentMatchIndex((prev) => (prev + 1) % matchCount);
    }
  };

  const goToPreviousMatch = () => {
    if (matchCount > 0) {
      setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % matchCount);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(text);
    toast.success('Copied!', {
      description: 'Text copied to clipboard',
    });
  };

  const contentWithSearch = (
    <YStack className="space-y-4 h-full flex flex-col min-h-0">
      <XStack className="items-center gap-2 flex-shrink-0">
        <div className="relative flex-1">
          <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <TextInput
            placeholder="Search within text..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentMatchIndex(0);
            }}
            className="pl-10"
          />
        </div>

        <Button
          onClick={copyToClipboard}
          variant="ghost"
          size="icon"
          tooltip="Copy text to clipboard"
        >
          <Icons.copy className="h-4 w-4" />
        </Button>

        {matchCount > 0 && (
          <XStack className="items-center gap-1">
            <Button
              onClick={goToPreviousMatch}
              variant="ghost"
              size="icon"
              disabled={matchCount === 0}
            >
              <Icons.chevronLeft className="h-4 w-4" />
            </Button>
            <Button
              onClick={goToNextMatch}
              variant="ghost"
              size="icon"
              disabled={matchCount === 0}
            >
              <Icons.chevronRight className="h-4 w-4" />
            </Button>
          </XStack>
        )}
      </XStack>

      {matchCount > 0 && (
        <div className="text-xs text-foreground-muted flex-shrink-0">
          {`${matchCount} matches found • Showing match ${currentMatchIndex + 1} of ${matchCount}`}
        </div>
      )}

      <ScrollArea
        ref={scrollAreaRef}
        className="w-full border rounded-md p-4 flex-1 min-h-0"
      >
        <div
          ref={textContentRef}
          className="text-sm leading-relaxed whitespace-pre-wrap break-words"
        >
          {highlightedContent}
        </div>
      </ScrollArea>
    </YStack>
  );

  const MetadataSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.entries(metadata!).map(([key, value]) => (
        <YStack
          key={key}
          className="gap-1 p-3 bg-surface rounded-md border border-border"
        >
          <div className="text-sm font-medium text-foreground">{key}</div>
          <div className="text-sm text-foreground-muted break-all">
            {String(value)}
          </div>
        </YStack>
      ))}
    </div>
  );

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[96vw] h-[96vh] max-w-[96vw] max-h-[96vh] flex flex-col min-h-0 overflow-hidden">
        <div className="w-full flex-shrink-0 text-xs text-foreground-muted mb-2">
          {`Input text length: ${textSize.toLocaleString()} characters`}
        </div>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full flex-1 min-h-0 flex flex-col space-y-4 overflow-hidden"
        >
          <TabsList className="w-fit flex-shrink-0">
            <TabsTrigger value="content">Content</TabsTrigger>
            {showDiffTab && <TabsTrigger value="diff">Diff</TabsTrigger>}
            {showMetadata && (
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            )}
          </TabsList>

          <TabsContent
            value="content"
            className="mt-4 flex-1 min-h-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            {contentWithSearch}
          </TabsContent>

          {showDiffTab && (
            <TabsContent
              value="diff"
              className="mt-4 flex-1 min-h-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
            >
              <div className="flex flex-col flex-1 min-h-0">
                <DiffSection
                  data={diffData}
                  isLoading={isDiffLoading}
                  error={diffError}
                  finalText={text}
                />
              </div>
            </TabsContent>
          )}

          {showMetadata && (
            <TabsContent
              value="metadata"
              className="space-y-4 mt-4 flex-1 min-h-0 overflow-auto data-[state=inactive]:hidden"
            >
              <MetadataSection />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </DialogPrimitive.Root>
  );
};
